import React from 'react';
import { Card } from "react-bootstrap";
import { Field, ErrorMessage } from 'formik';
import SelectField from '../common/SelectField';
import NumberField from '../common/NumberField';

/**
 * TestDetails Component - Handles test type selection, reason for testing, and related fields
 * Extracted from NikshayTest for better code organization and maintainability
 * 
 * @param {Object} props - Component props
 * @param {Object} props.values - Formik values object
 * @param {Function} props.handleChange - Formik handleChange function
 * @param {Function} props.setFieldValue - Formik setFieldValue function
 * @param {Array} props.testsWithoutSampleAvailability - Array of test types that don't show sample availability
 * @param {string} props.className - Additional CSS classes for the container
 */
const TestDetails = ({
  values,
  handleChange,
  setFieldValue,
  testsWithoutSampleAvailability = [],
  className = ""
}) => {
  return (
    <Card className={`${className}`}>
      <Card.Header className="bg-light p-3 mb-3">
        <h4 className="fw-bold mb-0 text-primary">Test Details</h4>
      </Card.Header>
      <Card.Body>
        {/* Reason for Testing - Required */}
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label>
                <span style={{ color: "red", fontSize: "20px" }}>*</span>
                Reason for Testing
              </label>
              <Field
                as="select"
                name="ReasonForTesting"
                className="form-control input-width select"
              >
                <option value="" disabled>Select Reason for testing</option>
                <option value="Diagnosis of TB">Diagnosis of TB</option>
                {/* <option value="Diagnosis of DRTB">Diagnosis of DRTB</option> */}
                {/* <option value="Follow-up of TB">Follow-up of TB</option> */}
                {/* <option value="Follow-up of DRTB">Follow-up of DRTB</option> */}
                {/* <option value="Diagnosis of TBI">Diagnosis of TBI</option> */}
              </Field>
              <ErrorMessage name="ReasonForTesting" component="div" className="text-danger" />
            </div>
          </div>
        </div>


        {/* SCENARIO A: Diagnosis of TB - Symptom Details */}
        {values.ReasonForTesting === "Diagnosis of TB" && (
          <div className="row">
            <SelectField
              name="PredominantSymptom"
              label="Predominant Symptom"
              required={false}
              colSize="col-md-4"
              placeholder="Select Predominant Symptom"
              options={[
                { value: "Cough", label: "Cough" },
                { value: "Fever", label: "Fever" },
                { value: "Weight Loss", label: "Weight Loss" },
                { value: "Night Sweat", label: "Night Sweat" },
                { value: "Blood in Sputum", label: "Blood in Sputum" },
                { value: "Others Extra Pulmonary", label: "Others Extra Pulmonary" },
              ]}
            />

            <div className="col-md-3">
              <NumberField
                name="PredominantSymptomDuration"
                label="Duration (in days)"
                placeholder="Enter duration in days"
              />
            </div>

            <div className="col-md-5">
              <NumberField
                name="HCPVisits"
                label="No. of HCP visited before diagnosis of current episode"
                placeholder="Enter number of HCP visits"
              />
            </div>
          </div>
        )}

        {/* SCENARIO B: Diagnosis of DRTB - Type of Presumptive DRTB */}
        {values.ReasonForTesting === "Diagnosis of DRTB" && (
          <div className="">
            <label className="form-label fw-bold mb-2">Type Of Presumptive DRTB:</label>
            <div className="row g-3">
              {[
                "At Diagnosis",
                "Contact of MDR/RR TB",
                "Any Follow-up +ve",
                "Private Referral",
                "Presumptive H Mono/Poly",
                "MDR/RR TB at Diagnosis",
                "Discordance resolution",
                "Failure or recurrence of MDR/RR-TB regimen",
                "Follow up Culture Positive"
              ].map((item, index) => (
                <div className="col-md-3 col-sm-6" key={index}>
                  <div className="form-check d-flex align-items-center gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`TypeOfPresumptiveDRTB_${index}`}
                      name="TypeOfPresumptiveDRTB"
                      value={item}
                      checked={values.TypeOfPresumptiveDRTB?.includes(item)}
                      onChange={handleChange}
                    />
                    <label
                      htmlFor={`TypeOfPresumptiveDRTB_${index}`}
                      className="form-check-label"
                      style={{ fontSize: "0.95rem", lineHeight: "1.2" }}
                    >
                      {item}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow Up Reason - Show for both Follow-up of TB and Follow-up of DRTB */}
        {(values.ReasonForTesting === "Follow-up of TB" || values.ReasonForTesting === "Follow-up of DRTB") && (
          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>
                  <span style={{ color: "red", fontSize: "20px" }}>*</span>
                  Follow Up Reason
                </label>
                <Field
                  as="select"
                  name="FollowUpReasonDSTB"
                  className="form-control input-width select"
                >
                  <option value="" disabled>Select Follow Up Reason</option>
                  
                  {/* Options for Follow-up of TB */}
                  {values.ReasonForTesting === "Follow-up of TB" && (
                    <>
                      <option value="End IP">End IP</option>
                      <option value="End CP">End CP</option>
                      <option value="Post Treatment 6M">Post Treatment 6M</option>
                      <option value="Post Treatment 12M">Post Treatment 12M</option>
                      <option value="Post Treatment 18M">Post Treatment 18M</option>
                      <option value="Post Treatment 24M">Post Treatment 24M</option>
                    </>
                  )}
                  
                  {/* Options for Follow-up of DRTB */}
                  {values.ReasonForTesting === "Follow-up of DRTB" && (
                    <>
                      <option value="Month">Month</option>
                      <option value="Post Treatment 6M">Post Treatment 6M</option>
                      <option value="Post Treatment 12M">Post Treatment 12M</option>
                      <option value="Post Treatment 18M">Post Treatment 18M</option>
                      <option value="Post Treatment 24M">Post Treatment 24M</option>
                    </>
                  )}
                </Field>
                <ErrorMessage name="FollowUpReasonDSTB" component="div" className="text-danger" />
              </div>
            </div>
          </div>
        )}

        {(values.ReasonForTesting === "Follow-up of DRTB" && values.FollowUpReasonDSTB === "Month") && (
          <div className="row mt-3">
            <div className="col-md-4">
              <NumberField
                name="FollowUpReasonDRTBMonth"
                label="Month"
                placeholder="Enter Month"
              />
            </div>
          </div>
        )}

        {/* Follow up Culture Positive: Months - Conditional field */}
        {values.TypeOfPresumptiveDRTB?.includes("Follow up Culture Positive") && (
          <div className="row mt-3">
            <div className="col-md-6">
              <NumberField
                name="CulturePositiveMonth"
                label="Follow up Culture Positive: Months"
                placeholder="Enter months"
              />
            </div>
          </div>
        )}

        {/* Type of Case - Required for both TB and DRTB */}
        <div className="row">
          {(values.ReasonForTesting === "Diagnosis of TB" || values.ReasonForTesting === "Diagnosis of DRTB") && (
            <SelectField
              name="TypeOfCase"
              label="Type of Case"
              required={true}
              colSize="col-md-4"
              placeholder="Select Type Of Case"
              options={[
                { value: "New", label: "New" },
                { value: "Retreatment: Others", label: "Retreatment: Others" },
                { value: "Retreatment: Recurrent", label: "Retreatment: Recurrent" },
                { value: "Retreatment: Treatment after failure", label: "Retreatment: Treatment after failure" },
                { value: "Retreatment: Treatment after lost to follow up", label: "Retreatment: Treatment after lost to follow up" },
              ]}
            />
          )}

          {/* Previous Treatment Details - Show only for retreatment cases */}
          {(values.TypeOfCase !== "New" && values.TypeOfCase !== "" && (values.ReasonForTesting === "Diagnosis of TB" || values.ReasonForTesting ===  "Diagnosis of DRTB")) && (
            <>
              <div className="col-md-4">
                <NumberField
                  name="MonthsOfTreatment"
                  label="Duration of Previous Treatment (in months)"
                  placeholder="Enter months"
                />
              </div>
              <div className="col-md-4">
                <NumberField
                  name="MonthsSinceEpisode"
                  label="Months since end of last episode"
                  placeholder="Enter months"
                />
              </div>
            </>
          )}
        </div>

        {/* TB Diagnosis checkboxes - ONLY for Diagnosis of TB */}
        {values.ReasonForTesting === "Diagnosis of TB" && (
          <div className="">
            <label className="form-label fw-bold mb-2">Diagnosis of TB</label>
            <div className="row g-3">
              {[
                "Presumptive",
                "TB Repeat Exam",
                "Private Referral",
                "Smear -ve & Chest X Ray suggestive of TB",
              ].map((item, index) => (
                <div className="col-md-3 col-sm-6" key={index}>
                  <div className="form-check d-flex align-items-center gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`DiagnosisOfTB_${index}`}
                      name="DiagnosisOfTB"
                      value={item}
                      checked={values.DiagnosisOfTB?.includes(item)}
                      onChange={handleChange}
                    />
                    <label
                      htmlFor={`DiagnosisOfTB_${index}`}
                      className="form-check-label"
                      style={{ fontSize: "0.95rem", lineHeight: "1.2" }}
                    >
                      {item}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Test Type Selection(for TB and DRTB) - Required */}
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label>
                <span style={{ color: "red", fontSize: "20px" }}>*</span>
                Test Type
              </label>
              <Field
                as="select"
                 className={`form-control input-width select ${!values.ReasonForTesting ? 'disabled-field' : ''}`}
                name="TestType"
                disabled={!values.ReasonForTesting || values.ReasonForTesting === ""}
                onChange={(e) => {
                  const newTestType = e.target.value;
                  // 1. Update TestType value
                  setFieldValue('TestType', newTestType);

                  if (testsWithoutSampleAvailability.includes(newTestType)) {
                    setFieldValue('SampleAvailability', "Absent");
                  }
                }}
              >
                <option value="" disabled> {!values.ReasonForTesting ? "Select Reason for Testing first" : "Select Test Type"}</option>
                {(values.ReasonForTesting === "Diagnosis of TB" || values.ReasonForTesting ===  "Diagnosis of DRTB") && (
                <>  
                {/* <option value="Microscopy ZN">Microscopy ZN</option>
                <option value="Microscopy Fluorescent">Microscopy Fluorescent</option> */}
                <option value="CBNAAT">CBNAAT</option>
                <option value="Truenat (MTB)">Truenat (MTB)</option>
                <option value="Truenat (MTB-RIF)">Truenat (MTB-RIF)</option>
                {/* <option value="Culture">Culture</option>
                <option value="FLLPA">FLLPA</option>
                <option value="SLLPA">SLLPA</option>
                <option value="DST">DST</option>
                <option value="Chest X Ray">Chest X Ray</option>
                <option value="Cytopathology">Cytopathology</option>
                <option value="Histopathology">Histopathology</option>
                <option value="Gene Sequencing">Gene Sequencing</option>
                <option value="Pathodetect - MTB, Rif & Inh">Pathodetect - MTB, Rif & Inh</option>
                <option value="Other">Other</option> */}
                </>)}

                 {/* Options for Follow-up of TB and Follow-up of DRTB */}
                {(values.ReasonForTesting === "Follow-up of TB" || values.ReasonForTesting === "Follow-up of DRTB") && (
                  <>  
                    <option value="Microscopy ZN">Microscopy ZN</option>
                    <option value="Microscopy Fluorescent">Microscopy Fluorescent</option>
                    <option value="Culture">Culture</option>
                    <option value="FLLPA">FLLPA</option>
                    <option value="SLLPA">SLLPA</option>
                    <option value="DST">DST</option>
                    <option value="Chest X Ray">Chest X Ray</option>
                    <option value="Cytopathology">Cytopathology</option>
                    <option value="Histopathology">Histopathology</option>
                    <option value="Gene Sequencing">Gene Sequencing</option>
                    <option value="Other">Other</option>
                  </>
                )}

                 {(values.ReasonForTesting ==="Diagnosis of TBI") && (
                <>              
                <option value="TST">TST</option>
                <option value="IGRA">IGRA</option>
                <option value="Cy-Tb">Cy-Tb</option>
                </>
                )} 
              </Field>
              <ErrorMessage name="TestType" component="div" className="text-danger" />
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default TestDetails;